/**
 * Purchase eSIM Modal — Country browser with real provider data
 * Fetches countries and packages from admin API endpoints
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { authFetch } from '../../auth';
import './PurchaseEsimModal.css';

const POPULAR_COUNTRIES = ['US', 'GB', 'FR', 'DE', 'JP', 'TH', 'ES', 'IT', 'AU', 'CA'];
const ADMIN_EMAIL = 'nfodor@mac.com';

const PurchaseEsimModal = ({ onClose, onSuccess, user }) => {
  const isAdmin = user && user.email && user.email.toLowerCase() === ADMIN_EMAIL;
  const [step, setStep] = useState('country'); // country, package, payment, processing, success
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [orderResult, setOrderResult] = useState(null);

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch('/api/esim/countries');
      if (response.ok) {
        const data = await response.json();
        setCountries(data.countries || []);
      } else {
        setError('Failed to load countries');
      }
    } catch (err) {
      setError('Network error loading countries');
      console.error('Error loading countries:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPackages = async (countryCode) => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`/api/esim/packages/country/${countryCode}`);
      if (response.ok) {
        const data = await response.json();
        setPackages(data.packages || []);
        setSelectedCountry(data.country);
      } else {
        setError('Failed to load packages');
      }
    } catch (err) {
      setError('Network error loading packages');
      console.error('Error loading packages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = (country) => {
    loadPackages(country.code);
    setStep('package');
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setStep('payment');
  };

  const handlePurchase = async () => {
    if (!selectedPackage) return;
    setStep('processing');
    setLoading(true);
    setError('');

    try {
      const response = await authFetch('/api/esim/pay/create-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          packageCode: selectedPackage.packageCode,
          provider: selectedPackage.provider
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          setError('No checkout URL returned');
          setStep('payment');
        }
      } else {
        const errData = await response.json();
        setError(errData.error || 'Failed to create checkout session');
        setStep('payment');
      }
    } catch (err) {
      console.error('Purchase error:', err);
      setError(err.message || 'Network error during purchase');
      setStep('payment');
    } finally {
      setLoading(false);
    }
  };

  // Direct order (no Stripe) — for admin testing
  const handleDirectOrder = async () => {
    if (!selectedPackage) return;
    setStep('processing');
    setLoading(true);
    setError('');

    try {
      const response = await authFetch('/api/esim/admin/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          packageCode: selectedPackage.packageCode,
          provider: selectedPackage.provider
        })
      });

      if (response.ok) {
        const data = await response.json();
        setOrderResult(data);
        setStep('success');
      } else {
        const errData = await response.json();
        setError(errData.error || 'Order failed');
        setStep('payment');
      }
    } catch (err) {
      console.error('Direct order error:', err);
      setError(err.message || 'Network error during order');
      setStep('payment');
    } finally {
      setLoading(false);
    }
  };

  // Filter countries by search
  const filteredCountries = countries.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
  });

  const popularCountries = countries.filter(c => POPULAR_COUNTRIES.includes(c.code));
  const otherCountries = filteredCountries.filter(c => !search || true); // show all when searching

  // Find best value package (lowest $/GB)
  const bestValueIdx = packages.length > 0
    ? packages.reduce((best, pkg, idx) => {
        if (pkg.dataAmount <= 0) return best;
        const pricePerGb = pkg.retailPrice / (pkg.dataAmount / (1024 * 1024 * 1024));
        const bestPricePerGb = packages[best].dataAmount > 0
          ? packages[best].retailPrice / (packages[best].dataAmount / (1024 * 1024 * 1024))
          : Infinity;
        return pricePerGb < bestPricePerGb ? idx : best;
      }, 0)
    : -1;

  return ReactDOM.createPortal(
    <div className="purchase-modal-overlay" onClick={onClose}>
      <div className="purchase-modal" onClick={(e) => e.stopPropagation()}>
        <div className="purchase-modal-header">
          <h2>Purchase eSIM</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="purchase-progress">
          <div className={`progress-step ${step === 'country' ? 'active' : step !== 'country' ? 'completed' : ''}`}>
            <span>1</span> Country
          </div>
          <div className={`progress-step ${step === 'package' ? 'active' : (step === 'payment' || step === 'processing') ? 'completed' : ''}`}>
            <span>2</span> Package
          </div>
          <div className={`progress-step ${step === 'payment' || step === 'processing' ? 'active' : ''}`}>
            <span>3</span> Payment
          </div>
        </div>

        <div className="purchase-modal-content">
          {error && (
            <div className="error-message">
              <span className="error-icon">!</span>
              {error}
              <button onClick={() => setError('')} className="dismiss-error">&times;</button>
            </div>
          )}

          {/* Step 1: Country Selection */}
          {step === 'country' && (
            <div className="country-selection">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>

              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading countries...</p>
                </div>
              ) : (
                <>
                  {/* Popular Countries */}
                  {!search && popularCountries.length > 0 && (
                    <div className="countries-section">
                      <h4>Popular Destinations</h4>
                      <div className="countries-grid">
                        {popularCountries.map(country => (
                          <button
                            key={country.code}
                            className="country-card"
                            onClick={() => handleCountrySelect(country)}
                          >
                            <div className="country-flag">{country.flag}</div>
                            <div className="country-name">{country.name}</div>
                            <div className="country-packages">{country.packageCount} plans</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All Countries */}
                  <div className="countries-section">
                    <h4>{search ? `Results for "${search}"` : 'All Countries'}</h4>
                    {otherCountries.length === 0 ? (
                      <p className="no-results">No countries found for "{search}"</p>
                    ) : (
                      <div className="countries-list">
                        {otherCountries.map(country => (
                          <button
                            key={country.code}
                            className="country-list-item"
                            onClick={() => handleCountrySelect(country)}
                          >
                            <span className="country-flag">{country.flag}</span>
                            <span className="country-name">{country.name}</span>
                            <span className="country-region">{country.region}</span>
                            <span className="country-packages">{country.packageCount} plans</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Package Selection */}
          {step === 'package' && (
            <div className="package-selection">
              <div className="selection-header">
                <button className="back-button" onClick={() => { setStep('country'); setSearch(''); }}>
                  &larr; Back
                </button>
                <h3>
                  {selectedCountry?.flag} {selectedCountry?.name}
                </h3>
                <p>{packages.length} packages available</p>
              </div>

              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading packages...</p>
                </div>
              ) : packages.length === 0 ? (
                <div className="no-results">No packages available for this country.</div>
              ) : (
                <div className="packages-grid">
                  {packages.map((pkg, idx) => (
                    <div
                      key={pkg.packageCode}
                      className={`package-card ${idx === bestValueIdx ? 'best-value' : ''}`}
                      onClick={() => handlePackageSelect(pkg)}
                    >
                      {idx === bestValueIdx && <div className="best-value-badge">Best Value</div>}
                      <div className="package-header">
                        <h4>{pkg.dataLabel}</h4>
                        <div className="package-price">${isAdmin ? pkg.wholesalePrice.toFixed(2) : pkg.retailPrice.toFixed(2)}</div>
                      </div>
                      <div className="package-details">
                        <div className="detail">
                          <span className="detail-label">Validity:</span>
                          <span className="detail-value">{pkg.validityDays} days</span>
                        </div>
                        <div className="detail">
                          <span className="detail-label">Network:</span>
                          <span className="detail-value">{pkg.network}</span>
                        </div>
                        {isAdmin && (
                        <div className="detail">
                          <span className="detail-label">Wholesale:</span>
                          <span className="detail-value wholesale">${pkg.wholesalePrice.toFixed(2)}</span>
                        </div>
                        )}
                        {pkg.features && pkg.features.length > 0 && (
                          <div className="package-features">
                            {pkg.features.map((f, i) => (
                              <span key={i} className="feature-tag">{f}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Payment */}
          {step === 'payment' && selectedPackage && (
            <div className="payment-section">
              <div className="selection-header">
                <button className="back-button" onClick={() => setStep('package')}>
                  &larr; Back
                </button>
                <h3>Order Summary</h3>
              </div>

              <div className="purchase-summary">
                <div className="summary-details">
                  <div className="summary-row">
                    <span>Country:</span>
                    <span>{selectedCountry?.flag} {selectedCountry?.name}</span>
                  </div>
                  <div className="summary-row">
                    <span>Package:</span>
                    <span>{selectedPackage.name}</span>
                  </div>
                  <div className="summary-row">
                    <span>Data:</span>
                    <span>{selectedPackage.dataLabel}</span>
                  </div>
                  <div className="summary-row">
                    <span>Validity:</span>
                    <span>{selectedPackage.validityDays} days</span>
                  </div>
                  <div className="summary-row">
                    <span>Network:</span>
                    <span>{selectedPackage.network}</span>
                  </div>
                  {isAdmin && (
                  <div className="summary-row">
                    <span>Provider:</span>
                    <span>{selectedPackage.provider}</span>
                  </div>
                  )}
                  <div className="summary-divider" />
                  {isAdmin ? (
                  <div className="summary-row total">
                    <span>Provider Cost:</span>
                    <span>${selectedPackage.wholesalePrice.toFixed(2)}</span>
                  </div>
                  ) : (
                  <div className="summary-row total">
                    <span>Price:</span>
                    <span>${selectedPackage.retailPrice.toFixed(2)}</span>
                  </div>
                  )}
                </div>
              </div>

              <div className="payment-options">
                {isAdmin ? (
                <>
                  <button
                    className="payment-button primary"
                    onClick={handleDirectOrder}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Order from Provider ($' + selectedPackage.wholesalePrice.toFixed(2) + ')'}
                  </button>
                  <div className="payment-security">
                    Debits provider account balance directly
                  </div>
                </>
                ) : (
                <>
                  <button
                    className="payment-button primary"
                    onClick={handlePurchase}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Pay Now ($' + selectedPackage.retailPrice.toFixed(2) + ')'}
                  </button>
                  <div className="payment-security">
                    <span className="security-icon">🔒</span>
                    Secure payment processing
                  </div>
                </>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Processing */}
          {step === 'processing' && (
            <div className="processing-section">
              <div className="processing-animation">
                <div className="spinner large"></div>
              </div>
              <h3>Processing Your Order</h3>
              <p>Contacting eSIM provider — this may take up to 30 seconds...</p>
              {error && (
                <div className="error-message" style={{ marginTop: 16 }}>
                  <span className="error-icon">!</span>
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Step 5: Success */}
          {step === 'success' && orderResult && (
            <div className="processing-section">
              <div style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</div>
              <h3>eSIM Order Created</h3>
              {orderResult.order && (
                <div className="purchase-summary" style={{ marginTop: 16 }}>
                  <div className="summary-details">
                    <div className="summary-row">
                      <span>Order #:</span>
                      <span>{orderResult.order.orderNo || 'Pending'}</span>
                    </div>
                    <div className="summary-row">
                      <span>Package:</span>
                      <span>{selectedPackage?.name || selectedPackage?.dataLabel}</span>
                    </div>
                    <div className="summary-row">
                      <span>Status:</span>
                      <span>{orderResult.order.isReady ? 'Ready' : 'Provisioning...'}</span>
                    </div>
                    {orderResult.order.qrCodeUrl && (
                      <div style={{ textAlign: 'center', marginTop: 12 }}>
                        <img src={orderResult.order.qrCodeUrl} alt="eSIM QR" style={{ width: 160, height: 160 }} />
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="payment-options" style={{ marginTop: 16 }}>
                <button className="payment-button primary" onClick={() => { onSuccess(orderResult.order); }}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PurchaseEsimModal;
